import React, { useState } from 'react'
import { round, vn } from 'vend-number'
import {
  IntegratedPaymentModal,
  PaymentData,
} from '../../../payments/integrated-payment/integrated-payment-modal/IntegratedPaymentModal'
import {
  AcceptStep,
  RejectStep,
} from '../../../payments/integrated-payment/integrated-payment-steps.constant'
import { useAppSelector } from '../../../../state/hooks'
import { selectCurrentSale } from '../../../../state/slices/currentSale/currentSaleSlice'
import {
  useRegisterManager,
  useRetailerSettingsManager,
} from '../../../../ng-compat/hooks'
import { formatToClientApi } from '../../../../functions/sale-transformer'
import { lineItemsAdapter } from '../../../../state/slices/currentSale/lineItemEntityAdapter'
import {
  useHandleCompleteIntegratedPayment,
  useHandleRejectedIntegratedPayment,
} from '../hooks/useHandleIntegrationPayment'
import { PartialAuthModal } from '../../../payments/partial-auth-modal/PartialAuthModal'
import { IntegratedPaymentOption, SalePayment } from './types'

interface Props {
  tenderAmount: string
  paymentOptions: IntegratedPaymentOption
  onSuccess: (result: SalePayment) => Promise<void>
  onClose: () => void
  updateAmountTendered: (amount?: number) => void
}

export const IntegratedPayment = ({
  paymentOptions,
  tenderAmount,
  onSuccess,
  onClose,
  updateAmountTendered,
}: Props) => {
  const sale = useAppSelector(selectCurrentSale)
  const { handleRejectedPayment } = useHandleRejectedIntegratedPayment()
  const outlet = useRegisterManager().getActiveOutlet()
  const discountProductId = useRetailerSettingsManager().get()
    .discount_product_id
  const [paymentData, setPaymentData] = useState<PaymentData>({
    currency: outlet.currency,
    retailerPaymentTypeId: '',
    amount: round(paymentOptions.amount, 2), //! For some integrated payment providers (SmartPay), it is important to have two digits behind the decimal points
    paymentType: paymentOptions.paymentType,
    registerId: paymentOptions.registerId,
    externalTransactionIds: [],
    registerSalePaymentIds: [],
    paymentId: paymentOptions.paymentId,
  })
  const [partialPayment, setPartialPayment] = useState<AcceptStep>()

  const {
    handleAcceptedPayment,
    handlePartialAuthRejected,
  } = useHandleCompleteIntegratedPayment(
    onSuccess,
    setPartialPayment,
    updateAmountTendered,
    setPaymentData,
    tenderAmount
  )

  const saleData = formatToClientApi(
    sale,
    lineItemsAdapter.getSelectors().selectAll(sale.lineItems),
    discountProductId
  )

  const handleClose = (val?: Promise<AcceptStep>) => {
    if (val) {
      val
        .then(step => {
          handleAcceptedPayment(paymentOptions.paymentType, tenderAmount, step)
        })
        .catch((rejection: RejectStep) => {
          handleRejectedPayment(paymentOptions.paymentType, rejection)
          onClose()
        })
    }
  }

  const handlePartialAuthClose = (applyPartialAuth: Promise<boolean>) => {
    applyPartialAuth.then(allowPartialAuth => {
      if (!allowPartialAuth && partialPayment) {
        handlePartialAuthRejected(paymentOptions.paymentType, partialPayment)
        return
      }
      onClose()
      updateAmountTendered()
    })
  }

  if (partialPayment) {
    return (
      <PartialAuthModal
        close={handlePartialAuthClose}
        approvedAmount={
          partialPayment.approved_amount
            ? vn(partialPayment.approved_amount).toNumber()
            : vn(tenderAmount).toNumber()
        }
      />
    )
  }

  return (
    <IntegratedPaymentModal
      saleData={saleData}
      paymentData={paymentData}
      close={handleClose}
    />
  )
}
